import { createReducer } from './utils.js';
import {
  GET_SURVEYS_REQUEST,
  GET_SURVEYS_SUCCESS,
  GET_SURVEYS_FAILURE,
  CREATE_SURVEY_REQUEST,
  CREATE_SURVEY_SUCCESS,
  CREATE_SURVEY_FAILURE,
  UPDATE_SURVEY_REQUEST,
  UPDATE_SURVEY_SUCCESS,
  UPDATE_SURVEY_FAILURE,
  DELETE_SURVEY_REQUEST,
  DELETE_SURVEY_SUCCESS,
  DELETE_SURVEY_FAILURE,
  TOGGLE_SURVEY_STATUS_REQUEST,
  TOGGLE_SURVEY_STATUS_SUCCESS,
  TOGGLE_SURVEY_STATUS_FAILURE,
  GET_SURVEY_RESPONSES_REQUEST,
  GET_SURVEY_RESPONSES_SUCCESS,
  GET_SURVEY_RESPONSES_FAILURE,
  SNACK_SET,
} from './constants.js';

const defaultState = {
  loading: false,
  errorMessage: null,
  surveys: [],
  creating: false,
  updating: false,
  deleting: false,
  toggling: false,
  responses: [],
  responsesLoading: false,
  responsesErrorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [GET_SURVEYS_REQUEST]: handleGetSurveysRequest,
  [GET_SURVEYS_SUCCESS]: handleGetSurveysSuccess,
  [GET_SURVEYS_FAILURE]: handleGetSurveysFailure,
  [CREATE_SURVEY_REQUEST]: handleCreateSurveyRequest,
  [CREATE_SURVEY_SUCCESS]: handleCreateSurveySuccess,
  [CREATE_SURVEY_FAILURE]: handleCreateSurveyFailure,
  [UPDATE_SURVEY_REQUEST]: handleUpdateSurveyRequest,
  [UPDATE_SURVEY_SUCCESS]: handleUpdateSurveySuccess,
  [UPDATE_SURVEY_FAILURE]: handleUpdateSurveyFailure,
  [DELETE_SURVEY_REQUEST]: handleDeleteSurveyRequest,
  [DELETE_SURVEY_SUCCESS]: handleDeleteSurveySuccess,
  [DELETE_SURVEY_FAILURE]: handleDeleteSurveyFailure,
  [TOGGLE_SURVEY_STATUS_REQUEST]: handleToggleSurveyStatusRequest,
  [TOGGLE_SURVEY_STATUS_SUCCESS]: handleToggleSurveyStatusSuccess,
  [TOGGLE_SURVEY_STATUS_FAILURE]: handleToggleSurveyStatusFailure,
  [GET_SURVEY_RESPONSES_REQUEST]: handleGetSurveyResponsesRequest,
  [GET_SURVEY_RESPONSES_SUCCESS]: handleGetSurveyResponsesSuccess,
  [GET_SURVEY_RESPONSES_FAILURE]: handleGetSurveyResponsesFailure,
});

function handleGetSurveysRequest(state) {
  return {
    ...state,
    surveys: [],
    loading: true,
    errorMessage: null,
  };
}

function handleGetSurveysSuccess(state, { payload: { surveys } }) {
  return {
    ...state,
    surveys: surveys,
    loading: false,
    errorMessage: null,
  };
}

function handleGetSurveysFailure(state, { payload: { error } }) {
  return {
    ...state,
    loading: false,
    errorMessage: error,
  };
}

function handleCreateSurveyRequest(state) {
  return {
    ...state,
    creating: true,
    errorMessage: null,
  };
}

function handleCreateSurveySuccess(state, { payload: { survey } }) {
  return {
    ...state,
    surveys: [...state.surveys, survey],
    creating: false,
    errorMessage: null,
  };
}

function handleCreateSurveyFailure(state, { payload: { error } }) {
  return {
    ...state,
    creating: false,
    errorMessage: error,
  };
}

function handleUpdateSurveyRequest(state) {
  return {
    ...state,
    updating: true,
    errorMessage: null,
  };
}

function handleUpdateSurveySuccess(state, { payload: { survey } }) {
  return {
    ...state,
    surveys: state.surveys.map(s => 
      s.id === survey.id ? survey : s
    ),
    updating: false,
    errorMessage: null,
  };
}

function handleUpdateSurveyFailure(state, { payload: { error } }) {
  return {
    ...state,
    updating: false,
    errorMessage: error,
  };
}

function handleDeleteSurveyRequest(state) {
  return {
    ...state,
    deleting: true,
    errorMessage: null,
  };
}

function handleDeleteSurveySuccess(state, { payload: { survey_id } }) {
  return {
    ...state,
    surveys: state.surveys.filter(s => s.id !== survey_id),
    deleting: false,
    errorMessage: null,
  };
}

function handleDeleteSurveyFailure(state, { payload: { error } }) {
  return {
    ...state,
    deleting: false,
    errorMessage: error,
  };
}

function handleToggleSurveyStatusRequest(state) {
  return {
    ...state,
    toggling: true,
    errorMessage: null,
  };
}

function handleToggleSurveyStatusSuccess(state, { payload: { surveys } }) {
  return {
    ...state,
    surveys: surveys,
    toggling: false,
    errorMessage: null,
  };
}

function handleToggleSurveyStatusFailure(state, { payload: { error } }) {
  return {
    ...state,
    toggling: false,
    errorMessage: error,
  };
}

function handleGetSurveyResponsesRequest(state) {
  return {
    ...state,
    responses: [],
    responsesLoading: true,
    responsesErrorMessage: null,
  };
}

function handleGetSurveyResponsesSuccess(state, { payload: { responses } }) {
  return {
    ...state,
    responses: responses,
    responsesLoading: false,
    responsesErrorMessage: null,
  };
}

function handleGetSurveyResponsesFailure(state, { payload: { error } }) {
  return {
    ...state,
    responsesLoading: false,
    responsesErrorMessage: error,
  };
}

// Action creators
export function getSurveys(tenant_id, sucursal_id) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_SURVEYS_REQUEST });

    try {
      const response = await dataSource.getSurveys(tenant_id, sucursal_id);
      dispatch({
        type: GET_SURVEYS_SUCCESS,
        payload: { surveys: response || [] },
      });
    } catch (error) {
      dispatch({
        type: GET_SURVEYS_FAILURE,
        payload: { error: error.message || 'Error al obtener encuestas' },
      });
      const snack = {
        open: true,
        message: 'Error al obtener encuestas: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export function createSurvey(surveyData) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: CREATE_SURVEY_REQUEST });

    try {
      const response = await dataSource.createSurvey(surveyData);
      dispatch({
        type: CREATE_SURVEY_SUCCESS,
        payload: { survey: response },
      });
      const snack = {
        open: true,
        message: 'Encuesta creada exitosamente',
        severity: 'success',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: true, survey: response };
    } catch (error) {
      dispatch({
        type: CREATE_SURVEY_FAILURE,
        payload: { error: error.message || 'Error al crear encuesta' },
      });
      const snack = {
        open: true,
        message: 'Error al crear encuesta: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: false, error: error.message };
    }
  };
}

export function updateSurvey(survey_id, surveyData) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: UPDATE_SURVEY_REQUEST });

    try {
      const response = await dataSource.updateSurvey(survey_id, surveyData);
      dispatch({
        type: UPDATE_SURVEY_SUCCESS,
        payload: { survey: response },
      });
      const snack = {
        open: true,
        message: 'Encuesta actualizada exitosamente',
        severity: 'success',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: true, survey: response };
    } catch (error) {
      dispatch({
        type: UPDATE_SURVEY_FAILURE,
        payload: { error: error.message || 'Error al actualizar encuesta' },
      });
      const snack = {
        open: true,
        message: 'Error al actualizar encuesta: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: false, error: error.message };
    }
  };
}

export function deleteSurvey(survey_id, tenant_id, sucursal_id) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: DELETE_SURVEY_REQUEST });

    try {
      await dataSource.deleteSurvey(survey_id);
      dispatch({
        type: DELETE_SURVEY_SUCCESS,
        payload: { survey_id },
      });
      const snack = {
        open: true,
        message: 'Encuesta eliminada exitosamente',
        severity: 'success',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: true };
    } catch (error) {
      dispatch({
        type: DELETE_SURVEY_FAILURE,
        payload: { error: error.message || 'Error al eliminar encuesta' },
      });
      const snack = {
        open: true,
        message: 'Error al eliminar encuesta: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: false, error: error.message };
    }
  };
}

export function toggleSurveyStatus(survey_id, tenant_id, sucursal_id) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: TOGGLE_SURVEY_STATUS_REQUEST });

    try {
      console.log('Toggling survey status:', { survey_id, tenant_id, sucursal_id });
      const response = await dataSource.toggleSurveyStatus(survey_id, tenant_id, sucursal_id);
      console.log('Toggle response:', response);
      
      dispatch({
        type: TOGGLE_SURVEY_STATUS_SUCCESS,
        payload: { surveys: response },
      });
      const snack = {
        open: true,
        message: 'Estado de encuesta cambiado exitosamente',
        severity: 'success',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: true, surveys: response };
    } catch (error) {
      console.error('Toggle survey status error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al cambiar estado de encuesta';
      dispatch({
        type: TOGGLE_SURVEY_STATUS_FAILURE,
        payload: { error: error.message || 'Error al cambiar estado de encuesta' },
      });
      const snack = {
        open: true,
        message: 'Error al cambiar estado de encuesta: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return { success: false, error: error.message };
    }
  };
}

export function getSurveyResponses(survey_id) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_SURVEY_RESPONSES_REQUEST });

    try {
      const response = await dataSource.getSurveyResponses(survey_id);
      dispatch({
        type: GET_SURVEY_RESPONSES_SUCCESS,
        payload: { responses: response || [] },
      });
    } catch (error) {
      dispatch({
        type: GET_SURVEY_RESPONSES_FAILURE,
        payload: { error: error.message || 'Error al obtener respuestas de encuesta' },
      });
      const snack = {
        open: true,
        message: 'Error al obtener respuestas de encuesta: ' + (error.message || String(error)),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}